import React, { useCallback, useEffect, useState } from 'react'

import numeral from 'numeral'
import { useLocation, useParams } from 'react-router-dom'

import {
  oneOfType,
  string,
  number,
} from 'prop-types'

import FileUpload from 'Pods/Shared/FileUpload'
import InputCheckbox from 'Pods/Shared/InputCheckbox'
import InputFieldWithPrefix from 'Pods/Shared/InputFieldWithPrefix'
import { run, RuleRunner } from 'Pods/Shared/RuleRunner'
import {
  required, min, minLength, maxLength, noEmoji,
} from 'Pods/Shared/Rules/Rules'
import TextareaField from 'Pods/Shared/TextareaField'

import CommissionNotice from '../../Shared/CommissionNotice'

const fieldValidations = [
  RuleRunner('price', 'Price', required, min(2)),
  RuleRunner('offer', 'Offer', required, minLength(10), maxLength(3000), noEmoji),
]

const MAX_COMMISSION = 20

const computeRevenue = (price, royaltyRate) => {
  const parsedPrice = Number(price)
  const commissionPercent = 100 - Number(royaltyRate)
  const percentageCommission = parsedPrice * commissionPercent / 100
  const commission = Math.min(percentageCommission, MAX_COMMISSION)

  return parsedPrice - commission
}

const deriveMode = (pathname) => {
  if (pathname.endsWith('/edit')) return 'edit'
  if (pathname.endsWith('/review')) return 'review'
  return 'apply'
}

function ApplicationForm({ jobBudget, designerRoyaltyRate, trackingId }) {
  const { pathname } = useLocation()
  const { id, application_id: applicationId } = useParams()
  const mode = deriveMode(pathname)

  const [state, setState] = useState(() => {
    const initial = {
      price: jobBudget,
      initialPrice: jobBudget,
      offer: '',
      attachmentIds: [],
      agreed_to_terms_of_use: false,
      designerRoyaltyRate,
      revenue: computeRevenue(jobBudget, designerRoyaltyRate),
      showErrors: false,
      disableButton: false,
      attachments: [],
    }
    return { ...initial, validationErrors: run(initial, fieldValidations) }
  })

  const onFetchApplicationSuccess = useCallback((response) => {
    const attributes = response.data.data.attributes

    setState((prev) => {
      const newState = { ...prev, initialPrice: attributes.finalPrice }

      if (mode === 'edit' || mode === 'review') {
        newState.offer = attributes.offer
      }

      newState.price = attributes.finalPrice
      newState.designerRoyaltyRate = attributes.designerRoyaltyRate
      newState.agreed_to_terms_of_use = attributes.agreedToTermsOfUse
      newState.revenue = computeRevenue(attributes.finalPrice, attributes.designerRoyaltyRate)
      newState.attachments = response.data.included
      newState.validationErrors = run(newState, fieldValidations)

      return newState
    })
  }, [mode])

  useEffect(() => {
    if (!applicationId) return

    axios.get(`/api/internal/job-applications/${applicationId}`)
      .then(onFetchApplicationSuccess)
      .catch(() => {})
  }, [applicationId, onFetchApplicationSuccess])

  const handleInputChange = (e) => {
    const { id: fieldId, value } = e.target

    setState((prev) => {
      const newState = { ...prev, [fieldId]: value }

      if (fieldId === 'price') {
        newState.revenue = computeRevenue(newState.price, newState.designerRoyaltyRate)
      }

      newState.validationErrors = run(newState, fieldValidations)
      return newState
    })
  }

  const handleCheckboxChange = (target) => {
    setState((prev) => ({ ...prev, [target]: !prev[target] }))
  }

  const handleCancelClick = (e) => {
    e.preventDefault()

    if (mode === 'apply') {
      window.location.href = '/3d-modeling-jobs/browse'
    } else {
      window.location.href = `/profile/projects/${id}`
    }
  }

  const handleGetAttachments = (list) => {
    setState((prev) => ({ ...prev, attachmentIds: list }))
  }

  const submitButtonAndUrlMap = () => {
    switch (mode) {
      case 'edit':
      case 'review':
        return {
          url: `/api/internal/jobs/${id}/applications/${applicationId}/update`,
          buttonText: 'Save',
        }
      case 'apply':
      default:
        return { url: `/api/internal/jobs/${id}/apply`, buttonText: 'Apply' }
    }
  }

  const onSubmitSuccess = () => {
    const parsedInitialPrice = parseFloat(state.initialPrice).toFixed(2)
    const parsedPrice = parseFloat(state.price).toFixed(2)

    window.customEventTracker({
      name: 'apply_for_3d_project',
      userId: App.config.userId || '',
      eventData: {
        project_id: trackingId,
        project_budget: parsedInitialPrice,
        offered_price: parsedPrice,
      },
    })

    window.location.href = `/profile/projects/${id}`
  }

  const onSubmitFailure = (error) => {
    App.events.publish('app:notification:flash:error', error.response.data.errors.join(', '))
    setState((prev) => ({ ...prev, disableButton: false }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    setState((prev) => ({ ...prev, showErrors: true, disableButton: true }))

    if (Object.getOwnPropertyNames(state.validationErrors).length === 0) {
      axios.put(submitButtonAndUrlMap().url, {
        data: {
          attributes: {
            price: state.revenue,
            final_price: state.price,
            offer: state.offer,
            attachment_ids: state.attachmentIds,
            agreed_to_terms_of_use: state.agreed_to_terms_of_use,
            comment: state.offer,
            offer_price: state.price,
            application_id: applicationId,
          },
        },
      })
        .then(onSubmitSuccess)
        .catch((error) => onSubmitFailure(error))
    } else {
      setState((prev) => ({ ...prev, disableButton: false }))
    }
  }

  const errorFor = (field) => state.validationErrors[field] || ''
  const sellerNoticeVariant = Number(state.designerRoyaltyRate) === 100 ? 'promo' : 'standard'

  return (
    <form className="create-project-form" onSubmit={handleSubmit}>
      <div className="your-offer">
        <h2 className="heading-18">Your offer</h2>
        <div className="row">
          <div className="col-6 col-md-3 col-lg-2">
            <InputFieldWithPrefix
              label="Price"
              id="price"
              name="price"
              type="number"
              prefix="$"
              required
              simpleError
              errorText={errorFor('price')}
              showError={state.showErrors}
              value={state.price}
              formClassName="form-group-grey"
              inputClassName="form-control-grey"
              onChange={handleInputChange}
            />
          </div>
          <div className="col-6 col-md-3 col-lg-2">
            <div className="form-group form-group-grey">
              <label>Your earnings:</label>
              <div className="create-project__earnings">{numeral(state.revenue).format('$0')}</div>
            </div>
          </div>
        </div>
        <div className="cgt-mb-20">
          <CommissionNotice audience="seller" variant={sellerNoticeVariant} />
        </div>
        <TextareaField
          id="offer"
          name="offer"
          rows={5}
          formClassName="form-group-grey"
          inputClassName="form-control-grey"
          errorText={errorFor('offer')}
          showError={state.showErrors}
          placeholder="Describe your experience and your proposal here..."
          value={state.offer}
          onChange={handleInputChange}
        />
      </div>

      <div className="form-group-grey">
        <FileUpload
          getAttachments={handleGetAttachments}
          attachments={state.attachments}
          ctaText="Upload your portfolio or examples of similar work here"
        />
      </div>

      {!applicationId && (
        <div className="form-group">
          <InputCheckbox
            id="agreed_to_terms_of_use"
            label="I agree with <a href='/pages/3d-projects-agreement' target='_blank'>Terms and Conditions</a>"
            checked={state.agreed_to_terms_of_use}
            onChange={handleCheckboxChange}
          />
        </div>
      )}

      <div className="footer u-text-right u-mt10">
        <button className="btn btn-fat btn-outline-default" onClick={handleCancelClick}>Cancel</button>
        <button
          className="btn btn-fat btn-primary u-ml10"
          type="submit"
          disabled={!state.agreed_to_terms_of_use || state.disableButton}
        >
          {submitButtonAndUrlMap().buttonText}
        </button>
      </div>
    </form>
  )
}

ApplicationForm.propTypes = {
  jobBudget: number.isRequired,
  designerRoyaltyRate: number.isRequired,
  trackingId: oneOfType([string, number]).isRequired,
}

export default ApplicationForm
